import { debounce } from 'lodash-es'
import { ref, computed, onMounted, nextTick, onUnmounted, type Ref, watch } from 'vue'

// 常量定义
const SCROLL_X_OFFSET = 100
const MIN_SCROLL_HEIGHT = 200
const BOTTOM_GAP = 16
const PAGINATION_GAP_SMALL = 56
const PAGINATION_GAP_LARGE = 64
const NO_PAGINATION_GAP = 16
const DEBOUNCE_DELAY = 150

interface UseTableUiOptions {
  disableXScroll?: boolean
  disableYScroll?: boolean
  size?: Ref<string | undefined>
  pagination?: any
}

/**
 * 获取表头高度
 */
function getTheadHeight(tableElement: HTMLElement | null): number {
  if (!tableElement) return 0
  const thead = tableElement.querySelector('.ant-table-thead')
  return thead?.getBoundingClientRect()?.height || 0
}

/**
 * 获取表尾高度
 */
function getFooterHeight(tableElement: HTMLElement | null): number {
  if (!tableElement) return 0
  const footer = tableElement.querySelector('.ant-table-footer')
  return footer?.getBoundingClientRect()?.height || 0
}

/**
 * 计算分页区域高度
 */
function getPaginationGap(size: string | undefined, hasPagination: boolean): number {
  if (!hasPagination) return NO_PAGINATION_GAP

  const isSmallOrMiddle = size === 'small' || size === 'middle'
  return isSmallOrMiddle ? PAGINATION_GAP_SMALL : PAGINATION_GAP_LARGE
}

/**
 * 计算垂直滚动高度
 */
function calculateScrollY(tableBodyTop: number, theadHeight: number, footerHeight: number, paginationGap: number): number {
  const totalReservedSpace = tableBodyTop + theadHeight + footerHeight + paginationGap + BOTTOM_GAP
  const availableHeight = window.innerHeight - totalReservedSpace
  return Math.max(availableHeight, MIN_SCROLL_HEIGHT)
}

/**
 * 计算水平滚动宽度
 */
function calculateScrollX(tableBodyWidth: number): number {
  return tableBodyWidth - SCROLL_X_OFFSET
}

export default (options: UseTableUiOptions) => {
  const tableRef = ref<HTMLElement | null>(null)
  const tableBodyRef = ref<HTMLElement | null>(null)
  const tableScrollY = ref(0)
  const tableScrollX = ref(0)

  /**
   * 根据配置计算表格滚动对象
   */
  const tableScroll = computed(() => {
    const { disableXScroll, disableYScroll } = options

    // 禁用所有滚动
    if (disableXScroll && disableYScroll) {
      return undefined
    }

    // 仅 Y 轴滚动
    if (disableXScroll) {
      return { y: tableScrollY.value }
    }

    // 仅 X 轴滚动
    if (disableYScroll) {
      return { x: tableScrollX.value }
    }

    // X 和 Y 轴都滚动
    return {
      x: tableScrollX.value,
      y: tableScrollY.value
    }
  })

  /**
   * 计算表格滚动尺寸
   */
  const calcTableScroll = () => {
    // 如果禁用了所有滚动，无需计算
    if (options.disableXScroll && options.disableYScroll) return

    const tableBody = tableBodyRef.value
    if (!tableBody) return

    requestAnimationFrame(() => {
      // 获取表格容器尺寸和位置
      const tableBodyRect = tableBody.getBoundingClientRect()

      // 仅在需要时计算 Y 轴滚动
      if (!options.disableYScroll) {
        const tableBodyTop = tableBodyRect.top
        const theadHeight = getTheadHeight(tableRef.value)
        const footerHeight = getFooterHeight(tableRef.value)
        const paginationGap = getPaginationGap(options.size?.value, options.pagination !== false)
        tableScrollY.value = calculateScrollY(tableBodyTop, theadHeight, footerHeight, paginationGap)
      }

      // 仅在需要时计算 X 轴滚动
      if (!options.disableXScroll) {
        const tableBodyWidth = tableBodyRect.width
        tableScrollX.value = calculateScrollX(tableBodyWidth)
      }
    })
  }

  // 防抖处理的计算函数
  const debouncedCalcScroll = debounce(calcTableScroll, DEBOUNCE_DELAY)

  // ResizeObserver 实例
  let resizeObserver: ResizeObserver | null = null

  /**
   * 监听影响布局的属性变化
   */
  watch(
    [() => options.size?.value],
    () => {
      calcTableScroll()
    },
    { flush: 'post' }
  )

  /**
   * 组件挂载时初始化
   */
  onMounted(() => {
    nextTick(() => {
      calcTableScroll()

      // 使用 ResizeObserver 监听容器尺寸变化
      const tableBody = tableBodyRef.value
      if (tableBody) {
        resizeObserver = new ResizeObserver(debouncedCalcScroll)
        resizeObserver.observe(tableBody)
      }

      // 同时监听窗口 resize（处理视口高度变化）
      window.addEventListener('resize', debouncedCalcScroll)
    })
  })

  /**
   * 组件卸载时清理
   */
  onUnmounted(() => {
    // 断开 ResizeObserver
    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
    window.removeEventListener('resize', debouncedCalcScroll)
  })

  return {
    tableRef,
    tableScroll,
    tableBodyRef,
    calcTableScroll
  }
}
